/**
 * Stride admin API (v2) — profile, password, password reset.
 * Auth login/logout live in authApi (same base URL).
 */

import { createApi } from '@reduxjs/toolkit/query/react'
import {
  mapAdminProfileDto,
  mapAdminProfileToUser,
  type AdminProfileDto,
} from '@/services/adminResponseMappers'
import { strideBaseQuery } from '@/services/rtkBaseQuery'
import type { User } from '@/types'

export interface ForgotPasswordRequest {
  email: string
}

export interface ResetPasswordRequest {
  token: string
  newPassword: string
}

export interface UpdateAdminMeRequest {
  name: string
}

export interface ChangeAdminPasswordRequest {
  currentPassword: string
  newPassword: string
}

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: strideBaseQuery,
  tagTypes: ['AdminProfile'],
  endpoints: builder => ({
    forgotPassword: builder.mutation<unknown, ForgotPasswordRequest>({
      query: body => ({
        url: '/admin/auth/forgot-password',
        method: 'POST',
        body,
      }),
    }),
    resetPassword: builder.mutation<unknown, ResetPasswordRequest>({
      query: body => ({
        url: '/admin/auth/reset-password',
        method: 'POST',
        body,
      }),
    }),
    getAdminMe: builder.query<AdminProfileDto, void>({
      query: () => ({ url: '/admin/me' }),
      transformResponse: (response: unknown) => mapAdminProfileDto(response),
      providesTags: ['AdminProfile'],
    }),
    updateAdminMe: builder.mutation<AdminProfileDto, UpdateAdminMeRequest>({
      query: body => ({
        url: '/admin/me',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: unknown) => mapAdminProfileDto(response),
      invalidatesTags: ['AdminProfile'],
    }),
    changeAdminPassword: builder.mutation<unknown, ChangeAdminPasswordRequest>({
      query: body => ({
        url: '/admin/me/password',
        method: 'PATCH',
        body: {
          currentPassword: body.currentPassword,
          newPassword: body.newPassword,
        },
      }),
    }),
  }),
})

export const {
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useGetAdminMeQuery,
  useLazyGetAdminMeQuery,
  useUpdateAdminMeMutation,
  useChangeAdminPasswordMutation,
} = adminApi

/** Convenience: API DTO as app User */
export function adminProfileDtoToUser(dto: AdminProfileDto): User {
  return mapAdminProfileToUser(dto)
}
